import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, User, FileText } from 'lucide-react';

const fieldClass =
  'px-3 py-2.5 w-full rounded-lg border border-gray-200 bg-gray-50 text-gray-900 text-sm focus:ring-2 focus:ring-[#372d83]/40 focus:border-[#372d83] outline-none';

function Field({ label, required, children }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-gray-600">
        {label}
        {required ? ' *' : ''}
      </label>
      {children}
    </div>
  );
}

function SectionCard({ icon: Icon, title, children, className = '' }) {
  return (
    <section
      className={`flex flex-col min-h-0 rounded-xl border border-gray-100 bg-gray-50/80 p-4 ${className}`}
    >
      <h3 className="flex items-center gap-2 text-sm font-semibold text-[#372d83] mb-3 shrink-0">
        {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden />}
        {title}
      </h3>
      <div className="space-y-3 flex-1">{children}</div>
    </section>
  );
}

export const emptyGymRequestForm = () => ({
  name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  owner_name: '',
  owner_email: '',
  owner_phone: '',
  vat_number: '',
  estimated_members: '',
  additionalInfo: '',
});

export default function GymAccessRequestForm({
  gymInfo,
  onChange,
  onSubmit,
  onCancel,
  isLoading,
  requestStatus,
}) {
  const { t } = useTranslation();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  useEffect(() => {
    if (requestStatus?.type === 'success') setPrivacyAccepted(false);
  }, [requestStatus]);

  return (
    <>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5 pb-4 border-b border-gray-100">
        <div className="text-left">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">{t('gymRequest.requestAccess')}</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">{t('gymRequest.forYourGym')}</p>
        </div>
        <p className="text-xs text-gray-500 sm:text-right shrink-0">{t('gymRequest.requiredHint')}</p>
      </div>

      {requestStatus?.message && (
        <div
          className={`p-4 mb-4 rounded-xl ${
            requestStatus.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          <p className="text-sm">{requestStatus.message}</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-stretch">
          <SectionCard icon={Building} title={t('gymRequest.sectionGym')}>
            <Field label={t('gymRequest.gymName')} required>
              <div className="relative">
                <Building className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  name="name"
                  value={gymInfo.name}
                  onChange={onChange}
                  placeholder={t('gymRequest.gymNamePlaceholder')}
                  className={`pl-9 pr-3 ${fieldClass}`}
                  required
                />
              </div>
            </Field>
            <Field label={t('gymRequest.contactEmail')} required>
              <input
                type="email"
                name="email"
                value={gymInfo.email}
                onChange={onChange}
                placeholder={t('gymRequest.emailPlaceholder')}
                className={fieldClass}
                required
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('gymRequest.phone')}>
                <input
                  type="tel"
                  name="phone"
                  value={gymInfo.phone}
                  onChange={onChange}
                  placeholder={t('gymRequest.phonePlaceholder')}
                  className={fieldClass}
                />
              </Field>
              <Field label={t('gymRequest.city')}>
                <input
                  type="text"
                  name="city"
                  value={gymInfo.city}
                  onChange={onChange}
                  placeholder={t('gymRequest.cityPlaceholder')}
                  className={fieldClass}
                />
              </Field>
            </div>
            <Field label={t('gymRequest.address')}>
              <input
                type="text"
                name="address"
                value={gymInfo.address}
                onChange={onChange}
                placeholder={t('gymRequest.addressPlaceholder')}
                className={fieldClass}
              />
            </Field>
          </SectionCard>

          <SectionCard icon={User} title={t('gymRequest.sectionOwner')}>
            <Field label={t('gymRequest.ownerName')} required>
              <div className="relative">
                <User className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  name="owner_name"
                  value={gymInfo.owner_name}
                  onChange={onChange}
                  placeholder={t('gymRequest.ownerNamePlaceholder')}
                  className={`pl-9 pr-3 ${fieldClass}`}
                  required
                />
              </div>
            </Field>
            <Field label={t('gymRequest.ownerEmail')}>
              <input
                type="email"
                name="owner_email"
                value={gymInfo.owner_email}
                onChange={onChange}
                placeholder={t('gymRequest.ownerEmailPlaceholder')}
                className={fieldClass}
              />
            </Field>
            <Field label={t('gymRequest.ownerPhone')}>
              <input
                type="tel"
                name="owner_phone"
                value={gymInfo.owner_phone}
                onChange={onChange}
                placeholder={t('gymRequest.ownerPhonePlaceholder')}
                className={fieldClass}
              />
            </Field>
          </SectionCard>

          <SectionCard
            icon={FileText}
            title={t('gymRequest.sectionExtra')}
            className="md:col-span-2 xl:col-span-1"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-1 gap-3">
              <Field label={t('gymRequest.vatNumber')}>
                <input
                  type="text"
                  name="vat_number"
                  value={gymInfo.vat_number}
                  onChange={onChange}
                  placeholder={t('gymRequest.vatPlaceholder')}
                  className={fieldClass}
                />
              </Field>
              <Field label={t('gymRequest.estimatedMembers')}>
                <input
                  type="number"
                  min="0"
                  name="estimated_members"
                  value={gymInfo.estimated_members}
                  onChange={onChange}
                  placeholder={t('gymRequest.estimatedMembersPlaceholder')}
                  className={fieldClass}
                />
              </Field>
            </div>
            <Field label={t('gymRequest.additionalInfo')}>
              <textarea
                name="additionalInfo"
                value={gymInfo.additionalInfo}
                onChange={onChange}
                placeholder={t('gymRequest.additionalInfoPlaceholder')}
                className={`p-3 min-h-[88px] xl:min-h-[120px] resize-y ${fieldClass}`}
                rows={3}
              />
            </Field>
          </SectionCard>
        </div>

        <div className="flex items-start gap-2.5 pt-1 text-xs leading-5 text-gray-600">
          <input
            id="gym-request-privacy"
            type="checkbox"
            checked={privacyAccepted}
            onChange={(event) => setPrivacyAccepted(event.target.checked)}
            required
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#372d83] focus:ring-[#372d83]"
          />
          <p>
            <label htmlFor="gym-request-privacy">dichiaro di aver preso visione della</label>{' '}
            <Link to="/privacy-policy" className="font-medium text-[#372d83] underline underline-offset-2">
              Privacy Policy
            </Link>
          </p>
        </div>

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2 border-t border-gray-100">
          <button
            type="button"
            onClick={onCancel}
            className="sm:w-40 py-2.5 px-4 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 transition"
          >
            {t('gymRequest.cancel')}
          </button>
          <button
            type="submit"
            disabled={isLoading || !privacyAccepted}
            className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-500 text-white py-2.5 px-4 text-sm font-medium hover:from-emerald-700 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-70 disabled:cursor-not-allowed transition"
          >
            {isLoading ? t('gymRequest.sending') : t('gymRequest.sendRequest')}
          </button>
        </div>
      </form>
    </>
  );
}
